import { PatientModel } from '@ctw/shared/api/fhir/models/patient';
import { PatientListItemModel } from '@ctw/shared/api/fhir/models/patient-list-item';
import {
  fqsRequest,
  type GraphqlPageInfo,
  MAX_OBJECTS_PER_REQUEST,
} from '@ctw/shared/api/fqs/client';
import {
  type PatientGraphqlResponse,
  type PatientListItemGraphqlResponse,
  patientsForBuilderQuery,
  patientsForUPIDQuery,
} from '@ctw/shared/api/fqs/queries/patients';
import {
  fetchResource,
  searchAllPagesOfFirstPartyRecords,
  searchBuilderRecords,
  searchFirstPartyRecords,
} from '@ctw/shared/api/fqs-rest/search-helpers';
import type { CTWRequestContext, CTWState } from '@ctw/shared/context/ctw-context';
import { useFeatureFlag } from '@ctw/shared/context/feature-flag-provider';
import { usePatientQuery } from '@ctw/shared/context/patient-provider';
import type { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { useCtwQuery } from '@ctw/shared/hooks/use-ctw-query';
import {
  QUERY_KEY_MATCHED_PATIENTS,
  QUERY_KEY_NON_THIRD_PARTY_PATIENTS_BY_UPID,
  QUERY_KEY_PATIENT_SEARCH,
  QUERY_KEY_PATIENTS_LIST_FQS,
} from '@ctw/shared/utils/query-keys';
import { sort } from '@ctw/shared/utils/sort';
import { hasNumber } from '@ctw/shared/utils/types';
import type { UseQueryResult } from '@tanstack/react-query';
import type { Patient } from 'fhir/r4';
import type { GraphQLClient } from 'graphql-request';
import { useMemo } from 'react';
import {
  collapsePatientSearchResults,
  type PatientSearchRow,
} from './collapse-patient-search-results';
import { filterByTags } from './resource-helper';
import {
  SYSTEM_SUMMARY,
  SYSTEM_ZUS_OWNER,
  SYSTEM_ZUS_SUMMARY,
  SYSTEM_ZUS_THIRD_PARTY,
  SYSTEM_ZUS_UPI_RECORD_TYPE,
} from './system-urls';
import type { Tag } from './types';

/**
 * Selects the best patient from a list of patients in priority order:
 * 1. Active patient owned by the user's builder
 * 2. Any active patient
 * 3. Named patient owned by the user's builder
 * 4. Any named patient (to avoid stubs/duplicates)
 * 5. Any patient owned by the user's builder
 * 6. Most recently updated patient
 *
 * Within each tier, the most recently updated patient wins.
 */
export function selectBestPatient(patients: Array<PatientModel>, builderId: string): PatientModel {
  if (patients.length === 0) {
    throw new Error('selectBestPatient: no patients provided');
  }

  const sortedPatients = sort(patients, 'resource.meta.lastUpdated', 'desc', true);
  const isActive = (p: PatientModel) => p.active === true;
  const hasName = (p: PatientModel) => (p.resource.name?.length ?? 0) > 0;
  const isOwned = (p: PatientModel) => p.ownedByBuilder(builderId);

  return (
    sortedPatients.find((p) => isActive(p) && isOwned(p)) ??
    sortedPatients.find(isActive) ??
    sortedPatients.find((p) => hasName(p) && isOwned(p)) ??
    sortedPatients.find(hasName) ??
    sortedPatients.find(isOwned) ??
    sortedPatients[0]
  );
}

// As of 1/9/24, if all of the patients for the given UPID are inactive, getPatientsForUPIDFQS will throw an
// unauthorized error so we return the patient used to initiate the query.
//
// When `crossBuilderLookup` is true, returns every non-third-party patient sharing the UPID
// (across builders the caller has access to). When false, restricts to records owned by the
// caller's builder — the legacy behavior. Callers read `ctw-cross-builder-upid-lookup` via
// `useFeatureFlag` and pass the result through.
export const useNonThirdPartyPatientsByUPID = (
  crossBuilderLookup: boolean,
): UseQueryResult<Array<PatientModel>> =>
  usePatientQuery({
    queryId: QUERY_KEY_NON_THIRD_PARTY_PATIENTS_BY_UPID,
    queryKey: [crossBuilderLookup],
    queryFn: async ({ graphqlClient, telemetry, requestContext, patient }) =>
      getPatientsForUPIDFQS(
        telemetry,
        graphqlClient,
        patient,
        crossBuilderLookup
          ? { tag: { nonematch: [SYSTEM_ZUS_THIRD_PARTY] } }
          : { tag: { allmatch: [`${SYSTEM_ZUS_OWNER}|builder/${requestContext.builderId}`] } },
      ).catch(() => [patient]),
  });

interface UseMatchedPatientsOptions {
  includeCurrentBuilder?: boolean;
}

export const useMatchedPatients = ({ includeCurrentBuilder }: UseMatchedPatientsOptions = {}) => {
  const filterFn = useMemo(
    () =>
      includeCurrentBuilder
        ? undefined
        : (requestContext: CTWRequestContext) => ({
            tag: {
              nonematch: [`${SYSTEM_ZUS_OWNER}|builder/${requestContext.builderId}`],
            },
          }),
    [includeCurrentBuilder],
  );

  return usePatientQuery({
    queryId: QUERY_KEY_MATCHED_PATIENTS,
    queryKey: [includeCurrentBuilder, filterFn],
    queryFn: async ({ graphqlClient, telemetry, requestContext, patient }) =>
      getPatientsForUPIDFQS(telemetry, graphqlClient, patient, filterFn?.(requestContext)),
  });
};

type FQSFilterMatchLogic = {
  allmatch?: Array<string>;
  nonematch?: Array<string>;
};

type FQSFilter = {
  ids?: FQSFilterMatchLogic;
  tag?: FQSFilterMatchLogic;
};

export async function getPatientsForUPIDFQS(
  telemetry: ReturnType<typeof useTelemetry>,
  graphqlClient: GraphQLClient,
  patient: PatientModel,
  filter: FQSFilter = {},
) {
  const tagHasAllmatch = filter.tag?.allmatch ?? false;
  const { data } = await fqsRequest<PatientGraphqlResponse>(
    telemetry,
    graphqlClient,
    patientsForUPIDQuery,
    {
      upid: patient.UPID,
      cursor: '',
      first: MAX_OBJECTS_PER_REQUEST,
      sort: {
        lastUpdated: 'DESC',
      },
      filter: tagHasAllmatch
        ? filter
        : {
            ...filter,
            tag: {
              ...filter.tag,
              nonematch: [SYSTEM_SUMMARY, ...(filter.tag?.nonematch ?? [])],
            },
          },
    },
  );
  let nodes = data.PatientConnection.edges.map((x) => x.node);

  // TODO: There's a bug in FQS that doesn't allow filtering with nonematch AND allmatch.
  // Once https://zeushealth.atlassian.net/browse/DRT-249 is resolved,
  // remove the below filter and use the filter in the query.
  if (tagHasAllmatch) {
    nodes = nodes.filter((node) => !node.meta?.tag?.some((t) => t.system === SYSTEM_ZUS_SUMMARY));
  }

  return nodes
    .filter(
      (node) =>
        !node.meta?.tag?.some(
          (t) => t.system === SYSTEM_ZUS_UPI_RECORD_TYPE && t.code === 'universal',
        ),
    )
    .map((node) => new PatientModel(node));
}

// Returns a single FHIR patient given a patientID and systemURL. When crossBuilderLookup
// is true, searches across all first-party records (so parent-scoped users can load
// patients that live in a child practice) and selectBestPatient prefers the user's-
// builder-owned record when one exists. When false, restricts to the user's builder —
// the legacy behavior. Callers in React code read `ctw-cross-builder-upid-lookup`
// via `useFeatureFlag` and pass the result through.
export async function getFirstPartyPatientByIdentifier(
  ctwFetch: CTWState['ctwFetch'],
  requestContext: CTWRequestContext,
  patientID: string,
  systemURL: string,
  crossBuilderLookup: boolean,
  tags?: Array<Tag>,
): Promise<PatientModel> {
  const params = {
    identifier: `${systemURL}|${patientID}`,
    _count: '50',
  };

  const baseContext = {
    env: requestContext.env,
    builderId: requestContext.builderId,
    authToken: requestContext.authToken,
  };

  const patients = crossBuilderLookup
    ? // Paginate across every page: under a UPID with many builder-owned copies, the
      // user's-builder-owned record may live past the first page, and selectBestPatient
      // can only pick from what we hand it.
      await searchAllPagesOfFirstPartyRecords(
        ctwFetch,
        {
          ...baseContext,
          // Zus staff users select a builder in the app; the Zus-Account header
          // overrides the builder carried in their JWT so requests act as the
          // selected builder. The header isn't supported for non-Zus-staff users —
          // their JWT-carried builder is what the API uses.
          zusAccount:
            requestContext.authTokenState.userType === 'zus' ? requestContext.builderId : undefined,
        },
        'Patient',
        params,
      )
    : await searchBuilderRecords(ctwFetch, baseContext, 'Patient', params);

  const filteredPatients = filterByTags(patients, tags);

  if (filteredPatients.length === 0) {
    throw new Error(
      `Failed to find patient information for patient from patientID ${patientID} with system ${systemURL}`,
    );
  }

  return selectBestPatient(
    filteredPatients.map((p) => new PatientModel(p)),
    requestContext.builderId,
  );
}

export async function getPatientByID(
  ctwFetch: CTWState['ctwFetch'],
  requestContext: CTWRequestContext,
  patientID: string,
): Promise<PatientModel> {
  const response = await fetchResource(
    ctwFetch,
    {
      env: requestContext.env,
      builderId: requestContext.builderId,
      authToken: requestContext.authToken,
    },
    'Patient',
    patientID,
  );

  return new PatientModel(response as Patient);
}

interface UsePatientsListData {
  patients: Array<PatientListItemModel>;
  pageInfo: GraphqlPageInfo;
}

export const usePatientsList = (pageSize: number, cursor: string) => {
  return useCtwQuery({
    queryId: QUERY_KEY_PATIENTS_LIST_FQS,
    queryKey: [pageSize, cursor],
    queryFn: async ({ requestContext, telemetry, graphqlClient }): Promise<UsePatientsListData> => {
      const { data } = await fqsRequest<PatientListItemGraphqlResponse>(
        telemetry,
        graphqlClient,
        patientsForBuilderQuery,
        {
          builderID: requestContext.builderId,
          cursor,
          first: pageSize,
          filter: {
            tag: {
              // Demo data still follows the old kludge pattern of tagging records as third party
              nonematch: [SYSTEM_ZUS_THIRD_PARTY],
            },
          },
        },
      );
      const patients = data.PatientConnection.edges.map((x) => new PatientListItemModel(x.node));
      return {
        patients,
        pageInfo: data.PatientConnection.pageInfo,
      };
    },
  });
};

// Search for 1st party patient records by name or identifier across all builders that
// you have access to. When the `ctw-cross-builder-upid-lookup` flag is on, scopes the
// builderID and Zus-Account header carefully so Zus-staff searches span all accessible
// builders. Parent-scoped builder users who have switched into a child builder get the
// search re-scoped to that child via the builderID query param. When the flag is off,
// falls back to the legacy behavior of always pinning the search to the user's
// builderId and never sending Zus-Account.
export function usePatientSearchList(searchValue: string | undefined) {
  const crossBuilderLookup = useFeatureFlag('ctw-cross-builder-upid-lookup');
  return useCtwQuery({
    queryId: QUERY_KEY_PATIENT_SEARCH,
    queryKey: [searchValue],
    queryFn: async ({ requestContext, ctwFetch }): Promise<Array<PatientSearchRow>> => {
      if (!searchValue) {
        return [];
      }

      const searchType: 'identifier' | 'name' = hasNumber(searchValue) ? 'identifier' : 'name';
      const isZusStaff = requestContext.authTokenState.userType === 'zus';
      // True for a parent-scoped builder user who has switched into a specific child
      // builder: their current builder differs from the JWT-assigned (parent) one.
      // Excludes Zus staff, whose JWT carries no builderId and who scope via the
      // Zus-Account header instead.
      const scopedToNonAssignedBuilder =
        !isZusStaff && requestContext.builderId !== requestContext.authTokenState.builderId;

      const params = crossBuilderLookup
        ? {
            _count: '100',
            [searchType]: searchValue,
            ...(scopedToNonAssignedBuilder && { builderID: requestContext.builderId }),
          }
        : {
            _count: '100',
            builderID: requestContext.builderId,
            [searchType]: searchValue,
          };

      const patients = await searchFirstPartyRecords(
        ctwFetch,
        {
          env: requestContext.env,
          builderId: requestContext.builderId,
          authToken: requestContext.authToken,
          ...(crossBuilderLookup && isZusStaff ? { zusAccount: requestContext.builderId } : {}),
        },
        'Patient',
        params,
      );

      const models = patients.map((patient) => new PatientModel(patient));
      return collapsePatientSearchResults(models, requestContext.builderId);
    },
    enabled: Boolean(searchValue),
  });
}
